import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import ScanLoader from "@/components/ScanLoader";
import { triggerChannelReverse, getChannelReverseJob } from "@/lib/api";

function nFmt(n) {
  if (n == null) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}k`;
  return String(n);
}

/**
 * CreateChannelModal — opens from a saved niche.
 *
 * Shows the AI-generated **Niche Playbook** for the reference channel
 * (hook patterns, content pillars, production profile, idea seeds), and
 * a single primary CTA: "✓ Create channel from this niche".
 *
 * That CTA navigates the user to the standard Create Channel wizard
 * with topic + style pre-filled. The wizard only generates the channel
 * KIT (avatar, banners, name + handle ideas, palette, about-page copy)
 * — it does NOT generate any video, voiceover, or script. After the kit
 * is ready the user is directed to Storyboard for content creation.
 */
export default function CreateChannelModal({ open, onClose, channel }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [fromCache, setFromCache] = useState(false);
  const pollRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => () => clearInterval(pollRef.current), []);

  useEffect(() => {
    if (open) {
      setResult(null);
      setFromCache(false);
    }
  }, [open, channel?.channel_id]);

  if (!open || !channel) return null;

  const run = async (force = false) => {
    setLoading(true);
    setResult(null);
    try {
      const r = await triggerChannelReverse(channel.channel_id, force);
      if (r.status === "complete" && r.result) {
        setResult(r.result);
        setFromCache(Boolean(r.from_cache));
        setLoading(false);
        if (force) toast.success("Re-analysed");
        return;
      }
      clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const job = await getChannelReverseJob(r.job_id);
          if (job.status === "complete") {
            clearInterval(pollRef.current);
            setResult(job.result);
            setFromCache(false);
            setLoading(false);
            toast.success("Niche playbook ready");
          } else if (job.status === "failed") {
            clearInterval(pollRef.current);
            setLoading(false);
            toast.error(job.error || "Niche analysis failed");
          }
        } catch {
          clearInterval(pollRef.current);
          setLoading(false);
          toast.error("Lost connection");
        }
      }, 4000);
    } catch (err) {
      setLoading(false);
      toast.error(err?.response?.data?.detail || "Couldn't start analysis");
    }
  };

  const playbook = result?.playbook;

  /** Derive a topic + style hint from the playbook so the Create
   * Channel wizard can be pre-filled without the user re-typing. */
  const buildPrefill = () => {
    const niche = channel.niche || "";
    const pillarLabels = (playbook?.content_pillars || [])
      .slice(0, 3)
      .map((p) => p.pillar)
      .filter(Boolean)
      .join(", ");
    const rawTopic = niche
      ? `${niche}${pillarLabels ? ` — covering ${pillarLabels}` : ""}`.trim()
      : pillarLabels;
    // Keep the topic textarea pre-fill tight so it doesn't dominate
    // the first paint. The full pillar context still flows through
    // via the style hint below.
    const topic = rawTopic.length > 120 ? `${rawTopic.slice(0, 117)}…` : rawTopic;
    const styleBits = [];
    if (playbook?.production_profile?.format) {
      styleBits.push(playbook.production_profile.format.replace(/_/g, " "));
    }
    if (playbook?.thumbnail_style?.summary) {
      styleBits.push(playbook.thumbnail_style.summary);
    }
    const style = styleBits.join(" · ").slice(0, 240);
    return { topic, style };
  };

  const startCreateChannel = () => {
    const { topic, style } = buildPrefill();
    const params = new URLSearchParams();
    if (topic) params.set("topic", topic);
    if (style) params.set("style", style);
    if (channel.channel_id) params.set("source_channel_id", channel.channel_id);
    if (channel.niche) params.set("niche", channel.niche);
    onClose?.();
    navigate(`/create-channel${params.toString() ? `?${params.toString()}` : ""}`);
  };

  return (
    <div
      data-testid="create-channel-modal"
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="min-h-screen flex items-start justify-center p-4 sm:p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-full max-w-4xl bg-zinc-950 border border-zinc-800 rounded-sm my-8">
          {/* Header */}
          <div className="px-6 py-5 border-b border-zinc-800 flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow-400 mb-2">
                // NICHE PLAYBOOK · REFERENCE CHANNEL
              </div>
              <h2 className="font-display text-2xl sm:text-3xl font-black text-white leading-tight truncate">
                {channel.name}
              </h2>
              <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500 mt-1">
                {nFmt(channel.subscribers)} subs · ${nFmt(channel.est_monthly_revenue)} / mo est
              </div>
            </div>
            <button
              data-testid="create-channel-modal-close-btn"
              onClick={onClose}
              className="font-mono text-sm text-zinc-500 hover:text-yellow-400 px-2 py-1 shrink-0"
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          {/* Primary CTA — Create channel from this niche.
              KIT ONLY: banner, avatar, name + handle, palette.
              NO video / script / voiceover is generated at this step. */}
          {playbook && !loading && (
            <div
              data-testid="create-channel-cta"
              className="px-6 py-5 border-b border-zinc-800 bg-yellow-400/[0.04]"
            >
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-yellow-400 font-bold mb-1">
                    // READY TO LAUNCH IN THIS NICHE?
                  </div>
                  <div className="text-sm text-zinc-200 leading-snug">
                    Generate your own channel <b>kit</b> for this niche —
                    avatar, banner art, name + handle ideas, palette,
                    about-page copy. No videos or scripts yet — you&apos;ll
                    create those in Storyboard.
                  </div>
                </div>
                <button
                  data-testid="create-channel-btn"
                  onClick={startCreateChannel}
                  className="font-display text-base font-black uppercase tracking-wide bg-yellow-400 text-black hover:bg-yellow-300 px-5 py-2.5 rounded-sm transition-colors shrink-0 shadow-[0_0_0_1px_rgba(250,204,21,0.5)]"
                >
                  ▸ Create channel from this niche
                </button>
              </div>
            </div>
          )}

          {/* Body */}
          <div className="px-6 py-6">
            {!playbook && !loading && (
              <div className="text-center py-10">
                <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow-400 mb-3">
                  [ AWAITING ANALYSIS ]
                </div>
                <p className="text-zinc-400 text-sm max-w-md mx-auto mb-6 leading-relaxed">
                  Claude will read this channel&apos;s top 25 videos and extract the
                  niche playbook — hook patterns, thumbnail rules, content
                  pillars, production profile, and upload cadence.
                </p>
                <button
                  data-testid="niche-playbook-run-btn"
                  onClick={() => run(false)}
                  className="bg-yellow-400 text-black font-semibold px-6 py-3 rounded-sm hover:bg-yellow-300 transition-colors"
                >
                  ▸ Analyse this niche
                </button>
              </div>
            )}

            {loading && (
              <ScanLoader
                stages={[
                  "Fetching channel metadata",
                  "Pulling top 25 uploads",
                  "Detecting hook patterns",
                  "Mapping thumbnail style",
                  "Drafting seed video ideas",
                ]}
                stageDurationMs={6000}
                subtitle="~30–60s · grounded on real upload data"
                logLines={[
                  `channels.list  → ${channel.name}`,
                  "playlistItems.list  → uploads",
                  "videos.list  → 30 items · part=snippet,statistics,contentDetails",
                  "→ sort by viewCount desc",
                  "→ slim payload · top 25 titles",
                  "claude-sonnet-4.5  · session opened",
                  "system: niche-research strategist",
                  "user: { channel, top_videos_by_views: [...] }",
                  "→ tokens streaming",
                  "→ parsing JSON · validating shape",
                  "→ extracting hook patterns",
                  "→ extracting thumbnail rules",
                  "→ extracting content pillars",
                  "→ caching playbook · 7d TTL",
                ]}
              />
            )}

            {playbook && !loading && (
              <div className="space-y-6">
                {/* TL;DR */}
                {playbook.tldr && (
                  <div className="border border-emerald-400/30 bg-emerald-500/5 p-4 rounded-sm">
                    <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-emerald-300 font-bold mb-2">
                      // TL;DR — WHY IT WORKS
                    </div>
                    <p className="text-sm text-zinc-100 leading-relaxed">{playbook.tldr}</p>
                  </div>
                )}

                {/* Hook patterns */}
                {playbook.hook_patterns?.length > 0 && (
                  <Section title={`Hook patterns (${playbook.hook_patterns.length})`}>
                    <div className="space-y-3">
                      {playbook.hook_patterns.map((h, i) => (
                        <div
                          key={i}
                          data-testid={`hook-pattern-${i}`}
                          className="border-l-2 border-yellow-400 pl-3 py-1"
                        >
                          <div className="font-display text-base font-bold text-white mb-1">
                            {String(i + 1).padStart(2, "0")} · {h.pattern}
                          </div>
                          {h.example_titles?.length > 0 && (
                            <ul className="text-xs text-zinc-300 mb-1 space-y-0.5">
                              {h.example_titles.slice(0, 3).map((t, j) => (
                                <li key={j} className="font-mono">→ {t}</li>
                              ))}
                            </ul>
                          )}
                          <div className="text-xs text-zinc-400 italic">
                            {h.why_it_works}
                          </div>
                        </div>
                      ))}
                    </div>
                  </Section>
                )}

                {/* Thumbnail style */}
                {playbook.thumbnail_style && (
                  <Section title="Thumbnail style">
                    <p className="text-sm text-zinc-100 mb-3">{playbook.thumbnail_style.summary}</p>
                    {playbook.thumbnail_style.rules?.length > 0 && (
                      <ul className="space-y-1.5">
                        {playbook.thumbnail_style.rules.map((r, i) => (
                          <li key={i} className="flex gap-2 text-xs text-zinc-300">
                            <span className="text-yellow-400 font-mono shrink-0">▸</span>
                            <span className="leading-relaxed">{r}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </Section>
                )}

                {/* Content pillars */}
                {playbook.content_pillars?.length > 0 && (
                  <Section title="Content pillars">
                    <div className="space-y-2">
                      {playbook.content_pillars.map((p, i) => (
                        <div key={i} className="flex items-center gap-3 text-sm">
                          <div className="font-display text-2xl font-black text-yellow-400 w-12 shrink-0">
                            {p.share_of_uploads_pct ?? "—"}
                            <span className="text-xs">%</span>
                          </div>
                          <div className="min-w-0">
                            <div className="text-white font-semibold">{p.pillar}</div>
                            {p.example && (
                              <div className="font-mono text-xs text-zinc-400 truncate">e.g. {p.example}</div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </Section>
                )}

                {/* Production + cadence */}
                <div className="grid sm:grid-cols-2 gap-4">
                  {playbook.production_profile && (
                    <Section title="Production profile">
                      <Row k="Format" v={playbook.production_profile.format} />
                      <Row k="Avg length" v={playbook.production_profile.avg_length_label} />
                      <Row k="On-camera" v={playbook.production_profile.faceless_or_oncamera?.replace("_", " ")} />
                      <Row k="Script" v={playbook.production_profile.scripted_or_freeform} />
                      {playbook.production_profile.ai_tools_likely?.length > 0 && (
                        <Row
                          k="Tools likely"
                          v={playbook.production_profile.ai_tools_likely.join(", ")}
                        />
                      )}
                    </Section>
                  )}
                  {playbook.upload_cadence && (
                    <Section title="Upload cadence">
                      <div className="font-display text-3xl font-black text-yellow-400 mb-1">
                        {playbook.upload_cadence.videos_per_week}
                        <span className="text-base text-zinc-400 font-normal"> / week</span>
                      </div>
                      <p className="text-xs text-zinc-300 leading-relaxed">
                        {playbook.upload_cadence.best_day_or_time_hint}
                      </p>
                    </Section>
                  )}
                </div>

                {/* Risks */}
                {playbook.risks_to_avoid?.length > 0 && (
                  <Section title="Risks to avoid" tone="warn">
                    <ul className="space-y-2 text-xs text-zinc-300">
                      {playbook.risks_to_avoid.map((r, i) => (
                        <li key={i} className="flex gap-2 leading-relaxed">
                          <span className="text-amber-400 shrink-0">⚠</span>
                          <span>{r}</span>
                        </li>
                      ))}
                    </ul>
                  </Section>
                )}

                <div className="flex items-center gap-3 pt-2 border-t border-zinc-900">
                  <button
                    data-testid="niche-playbook-rerun-btn"
                    onClick={() => run(true)}
                    className="font-mono text-xs uppercase tracking-[0.25em] text-yellow-400 border border-zinc-800 hover:border-yellow-400 px-4 py-2 rounded-sm transition-colors"
                  >
                    ↻ Re-analyse
                  </button>
                  {fromCache && (
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                      cached · valid 7 days
                    </span>
                  )}
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-600 ml-auto">
                    sample: {result?.sample_size || 0} videos
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children, tone }) {
  return (
    <div
      className={`border ${tone === "warn" ? "border-amber-500/30" : "border-zinc-800"} p-4 rounded-sm bg-black`}
    >
      <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-yellow-400 font-semibold mb-3">
        {title}
      </div>
      {children}
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex gap-3 text-xs py-1 border-b border-zinc-900 last:border-b-0">
      <span className="font-mono uppercase tracking-[0.2em] text-zinc-500 w-24 shrink-0">{k}</span>
      <span className="text-zinc-100 capitalize">{v || "—"}</span>
    </div>
  );
}
